import { addDays, formatDateISO } from '~/services/TimeService';

type CustomerPaymentMetadata = NonNullable<App.CustomOffer['payment_metadata']>;
export type LegacyPaymentType = CustomerPaymentMetadata['payment_type'];
export type PaymentFlowType = 'full_payment' | 'payment_schedule' | 'manual' | null | undefined;

export type PaymentFlowV2FieldErrorMap = Partial<
  Record<'manual_deposit_amount' | 'manual_balance_amount', { message?: string }>
>;

export type CustomerPaymentSectionState = ReturnType<typeof getCustomerPaymentSectionState>;

type CustomerPaymentSectionStateInput = {
  isExistingOffer: boolean;
  paymentFlowType: PaymentFlowType;
  legacyPaymentType: LegacyPaymentType;
  isCustomOfferPackage: boolean;
};

const toCents = (amount: number | null | undefined): number => Math.round(Number(amount ?? 0) * 100);
const fromCents = (amount: number | null | undefined): number => Number(amount ?? 0) / 100;

export type HydratedLegacyCustomerPaymentFormValues = {
  payment_type?: LegacyPaymentType;
  customer_deposit_1_amount?: number;
  customer_deposit_1_due_date?: string;
  customer_deposit_payment_made?: boolean;
  deposit_pi_number_customer?: string;
  final_amount_customer?: number;
};

export type HydratedPaymentFlowV2FormValues = {
  type: PaymentFlowType;
  payment_deadline?: string;
  balance_due_date?: string;
  deposit_amount: number;
  balance_amount: number;
  manual_deposit_amount: number;
  manual_deposit_due_date?: string;
  manual_deposit_payment_made: boolean;
  manual_deposit_pi_number: string;
  manual_balance_amount: number;
  manual_balance_due_date?: string;
};

export const hydrateLegacyCustomerPaymentFormValues = ({
  paymentMetadata,
  isLegacyFlow,
  defaultPaymentType,
  today,
}: {
  paymentMetadata?: CustomerPaymentMetadata;
  isLegacyFlow: boolean;
  defaultPaymentType: Exclude<LegacyPaymentType, null | undefined>;
  today: string;
}): HydratedLegacyCustomerPaymentFormValues => {
  if (!isLegacyFlow) {
    return {};
  }

  const paymentType = paymentMetadata?.payment_type ?? defaultPaymentType;

  return {
    payment_type: paymentType,
    customer_deposit_1_amount: fromCents(paymentMetadata?.customer_deposit_1_amount),
    customer_deposit_1_due_date: paymentMetadata?.customer_deposit_1_due_date ?? today,
    customer_deposit_payment_made: paymentMetadata?.customer_deposit_payment_made ?? false,
    deposit_pi_number_customer: paymentMetadata?.deposit_pi_number_customer ?? '',
    final_amount_customer: fromCents(paymentMetadata?.final_amount_customer),
  };
};

export const hydratePaymentFlowV2FormValues = (
  paymentFlowV2: CustomerPaymentMetadata['payment_flow_v2'],
  isNewOffer: boolean,
): HydratedPaymentFlowV2FormValues => {
  const paymentFlowType = paymentFlowV2?.type ?? (isNewOffer ? 'full_payment' : undefined);

  return {
    type: paymentFlowType,
    payment_deadline:
      paymentFlowV2?.payment_deadline ??
      (isNewOffer && paymentFlowType === 'full_payment' ? formatDateISO(addDays(1)) : undefined),
    balance_due_date: paymentFlowV2?.balance_due_date ?? undefined,
    deposit_amount: Number(paymentFlowV2?.deposit_amount ?? 0),
    balance_amount: Number(paymentFlowV2?.balance_amount ?? 0),
    manual_deposit_amount: Number(paymentFlowV2?.manual_deposit_amount ?? 0),
    manual_deposit_due_date: paymentFlowV2?.manual_deposit_due_date ?? undefined,
    manual_deposit_payment_made: paymentFlowV2?.manual_deposit_payment_made ?? false,
    manual_deposit_pi_number: paymentFlowV2?.manual_deposit_pi_number ?? '',
    manual_balance_amount: Number(paymentFlowV2?.manual_balance_amount ?? 0),
    manual_balance_due_date: paymentFlowV2?.manual_balance_due_date ?? undefined,
  };
};

export const calculateLegacyCustomerFinalPaymentAmount = ({
  price,
  customerDepositAmount,
  usesLegacyDepositCalculation,
}: {
  price: number | null | undefined;
  customerDepositAmount: number | null | undefined;
  usesLegacyDepositCalculation: boolean;
}): number => {
  const finalPaymentAmount = usesLegacyDepositCalculation
    ? Math.round(Number(price ?? 0) * 100 - Number(customerDepositAmount ?? 0) * 100)
    : Math.round(Number(price ?? 0) * 100);

  return finalPaymentAmount / 100 || 0;
};

export const calculatePaymentFlowV2DerivedAmounts = ({
  paymentFlowType,
  price,
  depositAmount,
  manualDepositAmount,
}: {
  paymentFlowType: PaymentFlowType;
  price: number | null | undefined;
  depositAmount: number | null | undefined;
  manualDepositAmount: number | null | undefined;
}): Pick<HydratedPaymentFlowV2FormValues, 'balance_amount' | 'manual_balance_amount'> => {
  switch (paymentFlowType) {
    case 'payment_schedule':
      return {
        balance_amount: Math.max(0, Number(price ?? 0) - Number(depositAmount ?? 0)),
        manual_balance_amount: 0,
      };
    case 'manual':
      return {
        balance_amount: 0,
        manual_balance_amount: Math.max(0, Number(price ?? 0) - Number(manualDepositAmount ?? 0)),
      };
    case 'full_payment':
      return {
        balance_amount: Math.max(0, Number(price ?? 0)),
        manual_balance_amount: 0,
      };
    default:
      return {
        balance_amount: 0,
        manual_balance_amount: 0,
      };
  }
};

export const getCustomerPaymentSectionState = ({
  isExistingOffer,
  paymentFlowType,
  legacyPaymentType,
  isCustomOfferPackage,
}: CustomerPaymentSectionStateInput) => {
  const isLegacyExistingFlow = isCustomOfferPackage || (isExistingOffer && !paymentFlowType);
  const isV2ManualFlow = paymentFlowType === 'manual';
  const isV2FullPayment = paymentFlowType === 'full_payment';
  const isV2PaymentSchedule = paymentFlowType === 'payment_schedule';
  const usesLegacyDepositCalculation = isLegacyExistingFlow && legacyPaymentType === 'deposit';

  return {
    isLegacyExistingFlow,
    isV2ManualFlow,
    isV2FullPayment,
    isV2PaymentSchedule,
    showLegacyPaymentTypeSelector: isLegacyExistingFlow,
    showV2PaymentFlowSelector: !isLegacyExistingFlow,
    showLegacyDepositFields: usesLegacyDepositCalculation,
    showLegacyFinalAmount: isLegacyExistingFlow,
    showV2ManualFields: isV2ManualFlow,
    usesLegacyDepositCalculation,
  };
};

export function deriveDueDateFromPaymentFlowV2(
  v2: HydratedPaymentFlowV2FormValues | CustomerPaymentMetadata['payment_flow_v2'] | undefined,
  fallback?: string,
): string {
  if (!v2?.type) {
    return fallback ?? formatDateISO(addDays(1));
  }
  if (v2.type === 'manual' && v2.manual_balance_due_date) {
    return formatDateISO(v2.manual_balance_due_date);
  }
  const deadline = v2.payment_deadline ?? (v2.type === 'payment_schedule' ? v2.balance_due_date : undefined);
  if (deadline) {
    return formatDateISO(deadline);
  }
  return fallback ?? formatDateISO(addDays(1));
}

export const buildCustomerPaymentMetadata = (
  paymentMetadata: CustomerPaymentMetadata,
): Partial<CustomerPaymentMetadata> => {
  const paymentFlowType = paymentMetadata.payment_flow_v2?.type;
  const paymentFlowV2 = paymentMetadata.payment_flow_v2;

  if (!paymentFlowType) {
    return {
      payment_type: paymentMetadata.payment_type,
      customer_deposit_1_due_date: paymentMetadata.customer_deposit_1_due_date
        ? formatDateISO(paymentMetadata.customer_deposit_1_due_date)
        : null,
      customer_deposit_1_amount: toCents(paymentMetadata.customer_deposit_1_amount),
      customer_deposit_payment_made: paymentMetadata.customer_deposit_payment_made ?? false,
      deposit_pi_number_customer: paymentMetadata.deposit_pi_number_customer ?? '',
      final_amount_customer: toCents(paymentMetadata.final_amount_customer),
    };
  }

  return {
    payment_flow_version: 'v2' as const,
    payment_flow_v2: {
      type: paymentFlowType,
      payment_deadline: paymentFlowV2?.payment_deadline ? formatDateISO(paymentFlowV2.payment_deadline) : undefined,
      deposit_amount: Number(paymentFlowV2?.deposit_amount ?? 0), // v2 amounts are in dollars (not cents)
      balance_amount: Number(paymentFlowV2?.balance_amount ?? 0), // v2 amounts are in dollars (not cents)
      balance_due_date: paymentFlowV2?.balance_due_date ? formatDateISO(paymentFlowV2.balance_due_date) : undefined,
      manual_deposit_amount: Number(paymentFlowV2?.manual_deposit_amount ?? 0),
      manual_deposit_due_date: paymentFlowV2?.manual_deposit_due_date
        ? formatDateISO(paymentFlowV2.manual_deposit_due_date)
        : undefined,
      manual_deposit_payment_made: paymentFlowV2?.manual_deposit_payment_made ?? false,
      manual_deposit_pi_number: paymentFlowV2?.manual_deposit_pi_number ?? '',
      manual_balance_amount: Number(paymentFlowV2?.manual_balance_amount ?? 0),
      manual_balance_due_date: paymentFlowV2?.manual_balance_due_date
        ? formatDateISO(paymentFlowV2.manual_balance_due_date)
        : undefined,
    },
  };
};
